import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  PageSection,
  Subtitle,
  StyledCard,
  H3,
  ControlContainerRow,
  Caption,
  ControlContainer,
} from 'common/styles/page';
import { useApiAdminV1RepositoryRepositoryFileShareListByOrganizationRetrieveQuery } from 'store/generated/admin/repository-file-share';
import { Loader, List, Icon } from 'semantic-ui-react';
import { getFileTypeIcon } from 'common/constants/filetypes';
import { StatusIcon } from 'common/components/StatusIcon';
import DoughnutGauge from 'areas/dashboard/components/DoughnutGauge';
import { getCurrentOrganizationId } from 'common/selectors/auth';

const FileTypes = {
  PDF: 0,
  MP4: 1,
  LINK: 2,
};

interface Props {
  onSelectMember?: (employeeId: string) => void;
  selectedFilename?: string | null;
}

const AnalyticsResourcesMain: React.FC<Props> = ({ onSelectMember, selectedFilename }) => {
  const organizationId = useSelector(getCurrentOrganizationId);

  const [sortKey, setSortKey] = useState<'employee_name' | 'option_name' | 'access_count'>(
    'employee_name'
  );
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedFile, setSelectedFile] = useState<string | null>(null);

  const { data: resourceData, isLoading } =
    useApiAdminV1RepositoryRepositoryFileShareListByOrganizationRetrieveQuery({ organizationId });

  const SortUp = (): JSX.Element => (
    <Icon name="caret up" style={{ margin: 0, marginTop: -8, zIndex: 10 }} id="label" />
  );

  const SortDown = (): JSX.Element => (
    <Icon name="caret down" style={{ margin: 0, marginTop: -8, zIndex: 10 }} id="label" />
  );

  const groupedByFilename = useMemo(() => {
    if (!Array.isArray(resourceData)) return {};
    return resourceData.reduce((acc: Record<string, any[]>, item) => {
      if (!acc[item.filename]) acc[item.filename] = [];
      acc[item.filename].push(item);
      return acc;
    }, {});
  }, [resourceData]);

  const categorizedResources = useMemo(() => {
    const files: Record<number, any[]> = {
      [FileTypes.PDF]: [],
      [FileTypes.MP4]: [],
      [FileTypes.LINK]: [],
    };
    Object.entries(groupedByFilename).forEach(([filename, items]) => {
      const type = items[0]?.file_type;
      if (type === FileTypes.PDF) files[FileTypes.PDF].push({ filename, file_type: type });
      else if (type === FileTypes.MP4) files[FileTypes.MP4].push({ filename, file_type: type });
      else files[FileTypes.LINK].push({ filename, file_type: type });
    });
    return files;
  }, [groupedByFilename]);

  useEffect(() => {
    if (!selectedFile) {
      if (selectedFilename && groupedByFilename[selectedFilename]) {
        setSelectedFile(selectedFilename);
      } else if (Object.keys(groupedByFilename).length > 0) {
        setSelectedFile(Object.keys(groupedByFilename)[0]);
      }
    }
  }, [groupedByFilename, selectedFilename, selectedFile]);

  const currentFileData = useMemo(
    () => (selectedFile ? groupedByFilename[selectedFile] || [] : []),
    [selectedFile, groupedByFilename]
  );

  const sortedData = useMemo(
    () =>
      [...currentFileData].sort((a, b) => {
        const aVal = a[sortKey] ?? '';
        const bVal = b[sortKey] ?? '';

        if (sortKey === 'access_count') {
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        }

        return sortDirection === 'asc'
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      }),
    [currentFileData, sortKey, sortDirection]
  );

  const percentAccessed = useMemo(() => {
    if (!currentFileData.length) return 0;
    const accessed = currentFileData.filter(entry => entry.access_count > 0).length;
    return (accessed / currentFileData.length) * 100;
  }, [currentFileData]);

  const renderListSection = (label: string, files: any[]) => {
    if (!files.length) return null;
    return (
      <div style={{ marginBottom: '1.5em' }}>
        <List.Header>
          <H3>{label}</H3>
        </List.Header>
        {files.map(({ filename, file_type }) => (
          <List.Item
            key={filename}
            active={filename === selectedFile}
            onClick={() => setSelectedFile(filename)}
            style={{
              cursor: 'pointer',
              background: filename === selectedFile ? '#e6f0fa' : 'transparent',
              borderRadius: '5px',
              padding: '6px',
              transition: 'background 0.15s, box-shadow 0.15s',
              boxShadow: filename === selectedFile ? '0 2px 8px rgba(80,150,255,0.10)' : undefined,
              fontWeight: filename === selectedFile ? 500 : undefined,
              color: filename === selectedFile ? '#2356a8' : undefined,
            }}
            onMouseEnter={(e: { currentTarget: { style: { background: string } } }) => {
              if (filename !== selectedFile) e.currentTarget.style.background = '#f5f7fa';
            }}
            onMouseLeave={(e: { currentTarget: { style: { background: string } } }) => {
              if (filename !== selectedFile) e.currentTarget.style.background = 'transparent';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {getFileTypeIcon(file_type)} {filename}
            </div>
          </List.Item>
        ))}
      </div>
    );
  };

  const noFilesAvailable =
    !categorizedResources[FileTypes.PDF].length &&
    !categorizedResources[FileTypes.MP4].length &&
    !categorizedResources[FileTypes.LINK].length;

  return (
    <PageSection width="100%" display="flex" alignItems="flex-start" gap="10px">
      <LeftColumn>
        <StyledCard as={LeftCard}>
          <FixedHeader>
            <Subtitle>Shared Resources</Subtitle>
          </FixedHeader>
          {isLoading ? (
            <Loader active inline="centered" />
          ) : noFilesAvailable ? (
            <PageSection padtop padleft padbottom>
              <p>No resources have been shared with your teams.</p>
            </PageSection>
          ) : (
            <ScrollableList>
              <List selection>
                {renderListSection('PDFs', categorizedResources[FileTypes.PDF])}
                {renderListSection('Videos', categorizedResources[FileTypes.MP4])}
                {renderListSection('Links', categorizedResources[FileTypes.LINK])}
              </List>
            </ScrollableList>
          )}
        </StyledCard>
      </LeftColumn>

      {!noFilesAvailable && (
        <RightColumn>
          <StyledCard>
            <PageSection padtop padleft padright padbottom>
              <PageSection flexDirection="column" padbottom="30px">
                <ControlContainerRow padright="10px">
                  <ControlContainer justify="left" flex="3">
                    <PageSection display="flex" flexDirection="column">
                      <Subtitle>{selectedFile || 'Selected File'}</Subtitle>
                      <Caption>Team member access history for this shared resource</Caption>
                    </PageSection>
                  </ControlContainer>
                  <ControlContainer justify="right">
                    <DoughnutGauge
                      complete={percentAccessed}
                      percent={true}
                      size="tiny"
                      max={100}
                      precision={0}
                      noWrap={true}
                    />
                  </ControlContainer>
                </ControlContainerRow>
              </PageSection>

              {isLoading ? (
                <Loader active inline="centered" />
              ) : selectedFile ? (
                <TableContainer>
                  <table
                    style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}
                  >
                    <colgroup>
                      <col style={{ width: '45%' }} />
                      <col style={{ width: '35%' }} />
                      <col style={{ width: '20%' }} />
                    </colgroup>
                    <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                      <tr>
                        {[
                          { key: 'employee_name', label: 'Member' },
                          { key: 'option_name', label: 'Team' },
                          { key: 'access_count', label: 'Access Count' },
                        ].map(col => {
                          const isActive = sortKey === col.key;
                          const showIcon = isActive ? (
                            sortDirection === 'asc' ? (
                              <SortUp />
                            ) : (
                              <SortDown />
                            )
                          ) : null;

                          return (
                            <th
                              key={col.key}
                              onClick={() => {
                                if (isActive) {
                                  setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
                                } else {
                                  setSortKey(col.key as any);
                                  setSortDirection('asc');
                                }
                              }}
                              style={{
                                cursor: 'pointer',
                                textAlign: 'left',
                                padding: '8px 12px',
                                verticalAlign: 'middle',
                                fontSize: '14px',
                                fontWeight: 500,
                                color: '#333',
                                alignItems: 'center',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                {col.label} {showIcon}
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                      <tr>
                        <td colSpan={3}>
                          <hr
                            style={{
                              border: 0,
                              height: '1px',
                              background: '#ccc',
                              marginTop: '1px',
                            }}
                          />
                        </td>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedData.map((entry, idx) => (
                        <tr
                          key={idx}
                          onClick={() => {
                            if (onSelectMember) onSelectMember(entry.employee_id);
                          }}
                          style={{
                            cursor: 'pointer',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={e => (e.currentTarget.style.background = '#f5f7fa')}
                          onMouseLeave={e => (e.currentTarget.style.background = '')}
                        >
                          <td
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '6px 0',
                            }}
                          >
                            <StatusIcon status={entry.access_count > 0 ? 5 : 1} size="large" />
                            {entry.employee_name}
                          </td>
                          <td style={{ padding: '6px 0' }}>{entry.option_name}</td>
                          <td
                            style={{
                              padding: '6px 0',
                              textAlign: 'center',
                              verticalAlign: 'middle',
                            }}
                          >
                            {entry.access_count === 0 ? (
                              <span style={{ color: '#bbb' }}>–</span>
                            ) : (
                              entry.access_count
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableContainer>
              ) : (
                <p>No file selected</p>
              )}
            </PageSection>
          </StyledCard>
        </RightColumn>
      )}
    </PageSection>
  );
};

export default AnalyticsResourcesMain;

const LeftColumn = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-right: 15px;

  @media (max-width: 768px) {
    flex: 1 1 100%;
  }
`;

const RightColumn = styled.div`
  flex: 3;
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (max-width: 768px) {
    flex: 1 1 100%;
  }
`;

const TableContainer = styled.div`
  max-height: 60vh;
  overflow-y: auto;
`;

const LeftCard = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: calc(100vh - 120px);
  overflow: hidden;
`;

const FixedHeader = styled.div`
  padding: 12px;
  flex-shrink: 0;
  background: white;
  border-bottom: 1px solid #ddd;
`;

const ScrollableList = styled.div`
  overflow-y: auto;
  padding: 8px 16px;
  flex-grow: 1;
`;
